import { Link, useHref, useNavigate } from "react-router-dom";
import { colors } from "../../style";
import {
  apiPlaceholder,
  formatDateTime,
  PROGRESS_COPY,
  STATUS,
} from "../../utils/meetingFormat";
import {
  callLength,
  directionFor,
  isLocalZone,
  shortZoneName,
} from "../calls/callFormat";
import { clamp, SentimentChip } from "../records/RecordList";
import { Icon, StatusDot } from "../ui";

export const COLUMNS = [
  "Date and time",
  "Person",
  "Number",
  "Direction",
  "Length",
  "Sentiment",
];

const cell = {
  whiteSpace: "nowrap",
  paddingBottom: "4px",
  verticalAlign: "top",
};

// The summary line's text and colour. `shown` is what to render for the
// summary, which for search hits is a highlighted snippet.
function summaryFor(call, shown) {
  const status = call.status?.value;
  const progress = PROGRESS_COPY[status];
  if (progress) {
    const busy = !!STATUS[status]?.busy;
    return {
      text: progress,
      color: busy ? colors.amberText : colors.errorText,
      busy,
    };
  }
  const stand = apiPlaceholder(call.summary);
  if (stand) return { text: stand, color: colors.textFaint };
  // Unsummarised calls send a transcript excerpt as the summary, so only a
  // real summary (or a search snippet) shows
  const text = shown ?? (call.has_summary ? call.summary : null);
  // Notes-only summaries can come back as whitespace
  const has = typeof text === "string" ? text.trim() : text;
  if (has) return { text, color: colors.textMuted };
  return call.has_summary
    ? { text: "Meeting notes only", color: colors.textFaint }
    : { text: "Not summarised yet", color: colors.textFaint };
}

// One processed call as a <tbody>: its cells, then a summary line across the
// table. Every call opens from anywhere in the row, busy and failed ones too,
// so their progress and errors show; the name is the real link (keyboard,
// middle-click). `shown` optionally swaps in highlighted nodes for
// name, number, extension, direction and summary.
const CallTranscriptionRow = ({ call, shown = {} }) => {
  const navigate = useNavigate();
  const ready = call.status?.value === "PROCESSED";
  const to = `/sidekick/calls/${encodeURIComponent(call.sqid)}`;
  const href = useHref(to);
  const zone = call.effective_timezone;
  const kind = directionFor(call.direction);

  const start = call.start_utc && new Date(call.start_utc);
  const valid = start && !Number.isNaN(start.getTime());
  const when = valid
    ? `${formatDateTime(start, zone)}${isLocalZone(zone) ? "" : ` ${shortZoneName(start, zone) ?? ""}`}`.trim()
    : call.start;

  const name = call.user_name ? (shown.name ?? call.user_name) : null;
  const ext = call.user_ext && (
    <span style={{ color: colors.textLight, fontWeight: 400 }}>
      {" "}
      ext {shown.extension ?? call.user_ext}
    </span>
  );
  const person = name ? (
    <>
      {name}
      {ext}
    </>
  ) : call.user_ext ? (
    <>Ext {shown.extension ?? call.user_ext}</>
  ) : (
    <span style={{ color: colors.textLight }}>Unknown</span>
  );

  const summary = summaryFor(call, shown.summary);

  // The name link handles its own clicks; modifier clicks open a tab like it
  const open = (e) => {
    if (e.target.closest("a")) return;
    if (window.getSelection?.().toString()) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey) {
      window.open(href, "_blank", "noopener");
      return;
    }
    navigate(to, { state: { fromList: true } });
  };

  return (
    <tbody className="sk-call-row sk-call-row-link" onClick={open}>
      <tr>
        <td
          style={cell}
          title={
            call.start && call.end
              ? `${call.start} to ${call.end}${zone ? ` (${zone})` : ""}`
              : undefined
          }
        >
          {when}
        </td>
        <td style={{ ...cell, fontWeight: 600, color: colors.text }}>
          <Link to={to} state={{ fromList: true }} className="sk-call-link">
            {person}
          </Link>
        </td>
        <td style={cell}>
          <span
            style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
          >
            <span title={kind.label} style={{ display: "inline-flex" }}>
              <Icon name={kind.icon} size={14} color={kind.color} />
            </span>
            {call.tel_number ? (shown.number ?? call.tel_number) : "Withheld"}
          </span>
        </td>
        <td style={cell}>{shown.direction ?? call.direction}</td>
        <td style={cell}>{callLength(call.duration)}</td>
        <td style={cell}>
          {ready && <SentimentChip sentiment={call.sentiment} size="sm" />}
        </td>
      </tr>
      <tr>
        <td
          colSpan={COLUMNS.length}
          style={{
            borderTop: "none",
            paddingTop: 0,
            paddingBottom: "12px",
            fontSize: "13px",
          }}
        >
          {summary && (
            <div
              className="sk-call-summary"
              style={{ ...clamp, color: summary.color }}
            >
              {summary.busy && (
                <StatusDot
                  pulse
                  size={7}
                  color={colors.amber}
                  title={STATUS[call.status.value].label}
                  style={{ marginRight: "7px", verticalAlign: "1px" }}
                />
              )}
              {summary.text}
            </div>
          )}
        </td>
      </tr>
    </tbody>
  );
};

export default CallTranscriptionRow;
